/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Email Interface Component
 * Provides advanced email management functionality with thread view, compose, and reply capabilities
 */
import React, { useState, useEffect, Fragment } from 'react';
import { Card, Badge, Button, Row, Col, Modal, Form, Dropdown, Placeholder, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faInbox,
  faPaperPlane,
  faClock,
  faEdit,
  faRobot,
  faSearch,
  faFilter,
  faEllipsisV,
  faReply,
  faForward,
  faTrash,
  faArchive,
  faStar,
  faCalendarAlt,
  faUser,
  faMagic,
  faLightbulb,
  faBrain,
  faArrowLeft,
  faChevronDown,
  faChevronUp,
  faTimes,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import { 
  UilInbox, 
  UilMessage, 
  UilClock,
  UilEdit,
  UilBrain,
  UilCheckCircle,
  UilCalendarAlt,
  UilUser,
  UilTrash,
  UilArchive,
  UilArrowLeft
} from '@iconscout/react-unicons';
import classNames from 'classnames';
import SearchBox from 'components/common/SearchBox';
import { useForm, SubmitHandler } from 'react-hook-form';
import { dateFormatter } from 'helpers/utils';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import './EmailInterface.css'; // Import custom responsive styles

interface EmailItem {
  id: string;
  subject: string;
  from: string;
  to: string;
  body?: string;  // Backend returns content in 'body' field, not 'body_raw'
  contentStatus?: string;  // Backend provides content status
  email_creation_date: string;
  direction: 'sent' | 'received';
  thread_id?: string;
  mail_id?: string;
  is_read?: boolean;
  is_starred?: boolean;
  is_ai_generated?: boolean;
  draft_confidence?: number;
}

interface EmailInterfaceProps {
  lead: any;
  emails: EmailItem[];
  onSendEmail: (emailData: any) => Promise<any>;
  onCreateDraft: (draftData: any) => Promise<any>;
  onReplyEmail: (emailId: string, replyData: any) => Promise<any>;
  onDeleteEmail: (emailId: string) => Promise<any>;
  onUpdateEmail: (emailId: string, updates: any) => Promise<any>;
  onRefreshEmails: (type: string) => Promise<any>;
  inboxCount: number;
  sentCount: number;
  draftCount: number;
  scheduledCount: number;
  status: string;
  onComposeClick?: () => void; // Optional callback for compose button
}

type EmailFormInputs = {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  message: string;
  schedule_date?: string;
  attachments?: FileList;
};

interface EmailRecipient {
  email: string;
  name?: string;
  initials?: string;
}

interface RecipientChipProps {
  recipient: EmailRecipient;
  onRemove: () => void;
  variant?: 'primary' | 'secondary' | 'success';
}

const RecipientChip: React.FC<RecipientChipProps> = ({ recipient, onRemove, variant = 'primary' }) => {
  const getInitials = (email: string, name?: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.split('@')[0].slice(0, 2).toUpperCase();
  };

  const initials = recipient.initials || getInitials(recipient.email, recipient.name);
  const displayName = recipient.name || recipient.email.split('@')[0];

  return (
    <div className={classNames(
      'd-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill border me-1 mb-1',
      `border-${variant}`,
      'recipient-chip'
    )} style={{ 
      backgroundColor: `var(--bs-${variant}-bg-subtle)`, 
      fontSize: '0.8rem',
      lineHeight: '1.2'
    }}>
      <div 
        className={classNames(
          'd-flex align-items-center justify-content-center rounded-circle text-white fw-bold',
          `bg-${variant}`
        )}
        style={{ width: '18px', height: '18px', fontSize: '0.6rem' }}
      >
        {initials}
      </div>
      <span className="text-truncate" style={{ fontSize: '0.8rem', maxWidth: '100px' }}>
        {displayName}
      </span>
      <Button
        variant="link"
        size="sm"
        className="p-0 text-muted"
        onClick={onRemove}
        style={{ fontSize: '0.7rem', width: '14px', height: '14px' }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </Button>
    </div>
  );
};

interface RecipientInputProps {
  label: string;
  recipients: EmailRecipient[];
  onAddRecipient: (recipient: EmailRecipient) => void;
  onRemoveRecipient: (index: number) => void;
  placeholder?: string;
  variant?: 'primary' | 'secondary' | 'success';
  required?: boolean;
  error?: string;
}

const RecipientInput: React.FC<RecipientInputProps> = ({
  label,
  recipients,
  onAddRecipient,
  onRemoveRecipient,
  placeholder = "Enter email address and press Enter",
  variant = 'primary',
  required = false,
  error
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addRecipient();
    }
  };

  const addRecipient = () => {
    const email = inputValue.trim();
    if (email && email.includes('@')) {
      const recipient: EmailRecipient = { email };
      onAddRecipient(recipient);
      setInputValue('');
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addRecipient();
    }
  };

  return (
    <div className="mb-2">
      <div className="d-flex align-items-center">
        <div className="flex-shrink-0 me-2" style={{ width: '32px' }}>
          <span className="text-muted fw-normal" style={{ fontSize: '0.9rem' }}>
            {label}:
          </span>
        </div>
        <div 
          className={classNames(
            'border rounded d-flex flex-wrap align-items-center flex-grow-1 px-2 py-1',
            error ? 'border-danger' : 'border-light'
          )}
          style={{ minHeight: '38px', backgroundColor: '#f8f9fa' }}
        >
          {recipients.map((recipient, index) => (
            <RecipientChip
              key={`${recipient.email}-${index}`}
              recipient={recipient}
              onRemove={() => onRemoveRecipient(index)}
              variant={variant}
            />
          ))}
          <Form.Control
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={recipients.length === 0 ? placeholder : ""}
            className="border-0 bg-transparent flex-grow-1 p-0"
            style={{ 
              minWidth: '150px', 
              boxShadow: 'none',
              fontSize: '0.9rem'
            }}
          />
        </div>
      </div>
      {error && <small className="text-danger ms-5">{error}</small>}
    </div>
  );
};

// Style constants
const emailBadgeStyle = {
  fontSize: 'clamp(0.65rem, 2vw, 0.7rem)',
  padding: '2px 6px',
  fontWeight: 500
} as const;

const EmailInterface: React.FC<EmailInterfaceProps> = ({
  lead,
  emails,
  status,
  onSendEmail,
  onCreateDraft,
  onReplyEmail,
  onDeleteEmail,
  onUpdateEmail,
  onRefreshEmails,
  inboxCount,
  sentCount,
  draftCount,
  scheduledCount,
  onComposeClick: externalComposeClick
}) => {
  const [activeTab, setActiveTab] = useState<'inbox' | 'sent' | 'drafts' | 'scheduled'>('inbox');
  const [selectedEmail, setSelectedEmail] = useState<EmailItem | null>(null);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [showReplyComposer, setShowReplyComposer] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'thread'>('list');
  const [expandedEmails, setExpandedEmails] = useState<string[]>([]);

  // Recipient state for compose
  const [toRecipients, setToRecipients] = useState<EmailRecipient[]>([]);
  const [ccRecipients, setCcRecipients] = useState<EmailRecipient[]>([]);
  const [bccRecipients, setBccRecipients] = useState<EmailRecipient[]>([]);

  // Recipient state for reply
  const [replyToRecipients, setReplyToRecipients] = useState<EmailRecipient[]>([]);
  const [replyCcRecipients, setReplyCcRecipients] = useState<EmailRecipient[]>([]);
  const [replyBccRecipients, setReplyBccRecipients] = useState<EmailRecipient[]>([]);

  const [showBccReply, setShowBccReply] = useState(false);
  const [showBccCompose, setShowBccCompose] = useState(false);

  // Attachment state
  const [attachments, setAttachments] = useState<File[]>([]);
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<EmailFormInputs>();

  const {
    register: registerReply,
    handleSubmit: handleSubmitReply,
    reset: resetReply,
    setValue: setValueReply,
    watch: watchReply,
    formState: { errors: errorsReply }
  } = useForm<EmailFormInputs>();

  const watchMessage = watch("message", "");
  const watchReplyMessage = watchReply("message", "");

  // Track if we're in the middle of an email operation to prevent skeleton loading
  const [skipRefreshFlag, setSkipRefreshFlag] = useState(false);
  
  // Ensure emails are loaded when component first mounts
  useEffect(() => {
    console.log('📧 [EmailInterface] Component mounted, checking if we need to load emails');
    console.log('📧 [EmailInterface] Current state:', { 
      emailsLength: emails.length, 
      activeTab, 
      status,
      inboxCount,
      skipRefreshFlag
    });
    
    // Only trigger refresh if not flagged to skip and we need to load emails
    // Removed emails.length from dependency to prevent infinite loops
    if (activeTab === 'inbox' && emails.length === 0 && status !== 'loading' && !skipRefreshFlag) {
      console.log('📧 [EmailInterface] No emails found on initial load, triggering inbox refresh');
      onRefreshEmails('received');
    }
  }, [activeTab]); // Added emails.length and inboxCount to dependencies

  const filteredEmails = emails.filter(email => {
    // Debug logging
    console.log(`📧 [EmailInterface] Filtering email: "${email.subject}"`);
    console.log(`📧 [EmailInterface] Email direction: ${email.direction}, activeTab: ${activeTab}`);
    console.log(`📧 [EmailInterface] Search query: "${searchQuery}"`);

    // Filter by tab
    const matchesTab = 
      (activeTab === 'inbox' && email.direction === 'received') ||
      (activeTab === 'sent' && email.direction === 'sent') ||
      (activeTab === 'drafts' && email.is_ai_generated) ||
      (activeTab === 'scheduled'); // Add a condition for scheduled if needed

    // Filter by search query if present
    const matchesSearch = searchQuery
      ? email.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        email.from?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        email.to?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        email.body?.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    // Debug the result
    console.log(`📧 [EmailInterface] Matches tab: ${matchesTab}, Matches search: ${matchesSearch}`);

    return matchesTab && matchesSearch;
  });

  const handleReplyClick = () => {
    if (selectedEmail) {
      setShowReplyComposer(true);
      setValueReply('to', selectedEmail.from);
      setValueReply('subject', `Re: ${selectedEmail.subject}`);
      
      // Initialize reply recipients
      setReplyToRecipients([{ email: selectedEmail.from }]);
      setReplyCcRecipients([]);
      setReplyBccRecipients([]);
      setShowBccReply(false);
    }
  };

  const handleComposeClick = () => {
    // If external compose handler is provided, use it instead of modal
    if (externalComposeClick) {
      externalComposeClick();
    } else {
      setShowComposeModal(true);
      
      // Initialize compose recipients with lead email if available
      if (lead?.lead_email) {
        setToRecipients([{ email: lead.lead_email, name: lead.lead_name }]);
      } else {
        setToRecipients([]);
      }
      setCcRecipients([]);
      setBccRecipients([]);
      setShowBccCompose(false);
    }
  };

  const recipientsToString = (recipients: EmailRecipient[]) => {
    return recipients.map(r => r.email).join(', ');
  };

  const handleFileAttachment = (files: FileList | null, isReply: boolean = false) => {
    if (files) {
      const fileArray = Array.from(files);
      if (isReply) {
        setReplyAttachments(prev => [...prev, ...fileArray]);
      } else {
        setAttachments(prev => [...prev, ...fileArray]);
      }
    }
  };

  const removeAttachment = (index: number, isReply: boolean = false) => {
    if (isReply) {
      setReplyAttachments(prev => prev.filter((_, i) => i !== index));
    } else {
      setAttachments(prev => prev.filter((_, i) => i !== index));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const onSubmitEmail: SubmitHandler<EmailFormInputs> = async (data) => {
    try {
      // Validate that we have at least one TO recipient
      if (toRecipients.length === 0) {
        toast.error('Please add at least one recipient');
        return;
      }

      const emailData = {
        ...data,
        to: recipientsToString(toRecipients),
        cc: ccRecipients.length > 0 ? recipientsToString(ccRecipients) : undefined,
        bcc: bccRecipients.length > 0 ? recipientsToString(bccRecipients) : undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        skipRefresh: true, // Pass the flag to prevent background refresh
      };

      // Set flags to prevent background refresh
      setIsSendingEmail(true);
      setSkipRefreshFlag(true);
      
      let result;
      if (data.schedule_date) {
        // Simulate network delay for scheduling
        await new Promise(resolve => setTimeout(resolve, 500));
        toast.success('Email scheduled successfully!');
      } else {
        // Send email without triggering a refresh
        result = await onSendEmail(emailData);
        toast.success('Email sent successfully!');
      }
      
      // Reset form and UI state
      reset();
      setShowComposeModal(false);
      
      // Reset recipients
      setToRecipients([]);
      setCcRecipients([]);
      setBccRecipients([]);
      setShowBccCompose(false);
      setAttachments([]);
    } catch (error) {
      // Extract and display the specific error message from the server if available
      const err = error as any; // Type assertion for error handling
      const errorMessage = 
        err.response?.data?.message || // Axios error format
        (err.message ? String(err.message) : null) || // Standard error message
        'Failed to send email';
      
      console.error('Email sending error:', err);
      toast.error(errorMessage);
    } finally {
      setIsSendingEmail(false);
      // Reset the refresh flag after a short delay to ensure UI updates are complete
      setTimeout(() => setSkipRefreshFlag(false), 1000);
    }
  };

  const onSubmitReply: SubmitHandler<EmailFormInputs> = async (data) => {
    if (!selectedEmail) return;
    
    try {
      // Validate that we have at least one TO recipient
      if (replyToRecipients.length === 0) {
        toast.error('Please add at least one recipient');
        return;
      }

      // Set flags to prevent background refresh
      setIsSendingReply(true);
      setSkipRefreshFlag(true);

      const replyData = {
        ...data,
        to: recipientsToString(replyToRecipients),
        cc: replyCcRecipients.length > 0 ? recipientsToString(replyCcRecipients) : undefined,
        bcc: replyBccRecipients.length > 0 ? recipientsToString(replyBccRecipients) : undefined,
        threadId: selectedEmail.thread_id,
        attachments: replyAttachments.length > 0 ? replyAttachments : undefined,
        skipRefresh: true, // Pass the flag to prevent background refresh
      };

      await onReplyEmail(selectedEmail.id, replyData);
      
      toast.success('Reply sent successfully!');
      resetReply();
      setShowReplyComposer(false);
      
      // Reset reply recipients
      setReplyToRecipients([]);
      setReplyCcRecipients([]);
      setReplyBccRecipients([]);
      setShowBccReply(false);
      setReplyAttachments([]);
    } catch (error) {
      // Extract and display the specific error message from the server if available
      const err = error as any; // Type assertion for error handling
      const errorMessage = 
        err.response?.data?.message || // Axios error format
        (err.message ? String(err.message) : null) || // Standard error message
        'Failed to send reply';
      
      console.error('Reply sending error:', err);
      toast.error(errorMessage);
    } finally {
      setIsSendingReply(false);
      // Reset the refresh flag after a short delay to ensure UI updates are complete
      setTimeout(() => setSkipRefreshFlag(false), 1000);
    }
  };

  const handleGenerateAIDraft = async () => {
    setIsGeneratingDraft(true);
    try {
      const aiDraft = {
        subject: `Follow up on ${lead.lead_name || 'your inquiry'}`,
        message: `Hi ${lead.lead_name || 'there'},\n\nI wanted to follow up on our recent conversation regarding your real estate needs. Based on our discussion, I believe I have some excellent properties that match your criteria.\n\nWould you be available for a brief call this week to discuss these opportunities?\n\nBest regards,\n[Your Name]`,
        is_ai_generated: true,
        draft_confidence: 0.95
      };
      
      setValue('subject', aiDraft.subject);
      setValue('message', aiDraft.message);
      
      toast.success('AI draft generated successfully!');
    } catch (error) {
      toast.error('Failed to generate AI draft');
    } finally {
      setIsGeneratingDraft(false);
    }
  };

  const handleEmailClick = (email: EmailItem) => {
    setSelectedEmail(email);
    setViewMode('thread');
    
    // Mark as read if it's an inbox email
    if (!email.is_read && email.direction === 'received') {
      onUpdateEmail(email.id, { is_read: true });
    }
  };

  const handleBackToList = () => {
    setSelectedEmail(null);
    setViewMode('list');
    setShowReplyComposer(false);
    resetReply();
  };



  const toggleEmailExpansion = (emailId: string) => {
    setExpandedEmails(prev => 
      prev.includes(emailId) 
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };

  const getEmailIcon = (email: EmailItem) => {
    if (email.is_ai_generated) return UilBrain;
    if (email.direction === 'sent') return UilMessage;
    return UilInbox;
  };

  const getEmailTypeColor = (email: EmailItem) => {
    if (email.is_ai_generated) return 'primary';
    if (email.direction === 'sent') return 'success';
    return 'info';
  };

  const getTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const TabButton = ({ type, icon, label, count, isActive, onClick }: any) => (
    <Button
      variant={isActive ? "primary" : "phoenix-secondary"}
      className={classNames(
        "d-flex align-items-center gap-1 px-2 px-sm-3 py-2 border-0 email-tab-btn",
        { "text-white": isActive, "text-muted": !isActive }
      )}
      onClick={onClick}
      style={{ fontSize: 'clamp(0.75rem, 2vw, 1rem)' }}
    >
      <FontAwesomeIcon icon={icon} />
      <span className="tab-label">{label}</span>
      <Badge 
        bg={isActive ? "light" : "primary"} 
        text={isActive ? "primary" : "white"}
        className="ms-1 d-flex align-items-center"
        style={{ 
          fontSize: 'clamp(0.65rem, 1.5vw, 0.8rem)',
        }}
      >
        {count}
      </Badge>
    </Button>
  );

  if (viewMode === 'thread' && selectedEmail) {
    return (
      <div className="modern-email-interface" style={{ width: '100%' }}>
        {/* Thread Header */}
        <div className="email-thread-header mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleBackToList}
                className="align-items-center gap-2"
              >
                <UilArrowLeft size="16" />
                Back to {activeTab}
              </Button>
              <div>
                <h4 className="mb-0 fw-semibold">{selectedEmail.subject || '(No Subject)'}</h4>
                <small className="text-muted">
                  Thread with {selectedEmail.direction === 'sent' ? selectedEmail.to : selectedEmail.from}
                </small>
              </div>
            </div>
            
            <div className="d-flex gap-2 justify-content-end flex-nowrap">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleReplyClick}
                className="align-items-center gap-2"
              >
                <FontAwesomeIcon icon={faReply} />
                Reply
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="align-items-center gap-2"
              >
                <FontAwesomeIcon icon={faArchive} />
                Archive
              </Button>
              {/* {selectedEmail.mail_id && (
                <div>
                  <Button
                  variant="light"
                  size="sm"
                  href={`https://mail.google.com/mail/u/0/#inbox/${selectedEmail.mail_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-flex align-items-center gap-2"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                  Open in Gmail
                </Button>
                </div>
                
              )} */}
            </div>
          </div>
        </div>

        {/* Reply Composer - Shows at top when replying */}
        {showReplyComposer && (
          <Card className="reply-composer-card mb-4">
            <Card.Header className="bg-primary text-white">
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="mb-0 d-flex align-items-center gap-2 text-white">
                  <FontAwesomeIcon icon={faReply} />
                  Reply to {selectedEmail.from}
                </h6>
                <Button
                  variant="link"
                  size="sm"
                  className="text-white p-0"
                  onClick={() => {
                    setShowReplyComposer(false);
                    resetReply();
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              <Form onSubmit={handleSubmitReply(onSubmitReply)}>
                <div className="condensed-email-form">
                  {/* TO and CC Fields */}
                  <div className="recipient-fields mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="flex-grow-1">
                        <RecipientInput
                          label="To"
                          recipients={replyToRecipients}
                          onAddRecipient={(recipient) => setReplyToRecipients([...replyToRecipients, recipient])}
                          onRemoveRecipient={(index) => setReplyToRecipients(replyToRecipients.filter((_, i) => i !== index))}
                          placeholder="Enter recipient email address"
                          variant="primary"
                          required
                          error={errorsReply.to?.message}
                        />
                      </div>
                      {!showBccReply && (
                        <Button
                          variant="link"
                          size="sm"
                          className="text-decoration-none ms-3 p-0"
                          onClick={() => setShowBccReply(true)}
                          style={{ fontSize: '0.85rem', color: '#6c757d' }}
                        >
                          BCC
                        </Button>
                      )}
                    </div>
                    
                    <RecipientInput
                      label="Cc"
                      recipients={replyCcRecipients}
                      onAddRecipient={(recipient) => setReplyCcRecipients([...replyCcRecipients, recipient])}
                      onRemoveRecipient={(index) => setReplyCcRecipients(replyCcRecipients.filter((_, i) => i !== index))}
                      placeholder="Add CC recipients"
                      variant="secondary"
                    />
                    
                    {showBccReply && (
                      <RecipientInput
                        label="Bcc"
                        recipients={replyBccRecipients}
                        onAddRecipient={(recipient) => setReplyBccRecipients([...replyBccRecipients, recipient])}
                        onRemoveRecipient={(index) => setReplyBccRecipients(replyBccRecipients.filter((_, i) => i !== index))}
                        placeholder="Add BCC recipients"
                        variant="success"
                      />
                    )}
                  </div>
                  
                  {/* Subject Field */}
                  <div className="mb-3">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 me-2" style={{ width: '65px' }}>
                        <span className="text-muted fw-normal" style={{ fontSize: '0.9rem' }}>
                          Subject:
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <Form.Control
                          type="text"
                          placeholder="Email subject"
                          {...registerReply("subject", { required: "Subject is required" })}
                          className="border-light bg-light"
                          style={{ fontSize: '0.9rem' }}
                        />
                        {errorsReply.subject && (
                          <small className="text-danger ms-0">{errorsReply.subject.message}</small>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <Col md="12">
                    <Form.Label className="fw-semibold">Message</Form.Label>
                    <ReactQuill
                      style={{ height: "200px", marginBottom: "3rem" }}
                      theme="snow"
                      value={watchReplyMessage}
                      onChange={(value) => setValueReply("message", value)}
                      placeholder="Write your reply here..."
                    />
                  </Col>
                  
                  {/* Attachments Section */}
                  <Col md="12">
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="text-muted fw-normal" style={{ fontSize: '0.9rem' }}>
                          Attachments:
                        </span>
                        <div>
                          <input
                            type="file"
                            multiple
                            onChange={(e) => handleFileAttachment(e.target.files, true)}
                            style={{ display: 'none' }}
                            id="reply-file-input"
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => document.getElementById('reply-file-input')?.click()}
                            className="d-flex align-items-center gap-1"
                          >
                            <FontAwesomeIcon icon={faPaperPlane} size="sm" />
                            Attach Files
                          </Button>
                        </div>
                      </div>
                      
                      {replyAttachments.length > 0 && (
                        <div className="attachment-list">
                          {replyAttachments.map((file, index) => (
                            <div key={index} className="attachment-item d-flex align-items-center justify-content-between p-2 border rounded mb-1 bg-light">
                              <div className="d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon={faPaperPlane} className="text-muted" />
                                <div>
                                  <span className="fw-medium" style={{ fontSize: '0.85rem' }}>{file.name}</span>
                                  <small className="text-muted ms-2">({formatFileSize(file.size)})</small>
                                </div>
                              </div>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => removeAttachment(index, true)}
                                className="text-danger p-0"
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Col>
                  
                  <Col md="12">
                    <div className="d-flex justify-content-between align-items-center">
                      <Button
                        variant="outline-primary"
                        onClick={handleGenerateAIDraft}
                        disabled={isGeneratingDraft}
                        className="d-flex align-items-center gap-2"
                      >
                        <FontAwesomeIcon icon={faBrain} />
                        {isGeneratingDraft ? 'Generating...' : 'AI Assist'}
                      </Button>
                      
                      <div className="d-flex gap-2">
                        <Button 
                          variant="secondary" 
                          onClick={() => {
                            setShowReplyComposer(false);
                            resetReply();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          variant="primary"
                          disabled={!watchReplyMessage.trim() || isSendingReply}
                          onClick={handleSubmitReply(onSubmitReply)}
                        >
                          {isSendingReply ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              Sending...
                            </>
                          ) : (
                            'Send Reply'
                          )}
                        </Button>
                      </div>
                    </div>
                  </Col>
                </div>
              </Form>
            </Card.Body>
          </Card>
        )}

        {/* Email Thread Content */}
        <div className="email-thread-content w-100" style={{maxWidth: "100%"}}>
          <Card className="thread-email-card w-100 border-0" style={{maxWidth: "100%"}}>
            <Card.Body className="email-card-body p-4 w-100">
              {/* Email Header */}
              <div className="thread-email-header mb-4">
                <div className="d-flex align-items-start justify-content-between">
                  <div className="d-flex align-items-start gap-3">
                    <div className="thread-avatar">
                      <div className={classNames(
                        'avatar-circle d-flex align-items-center justify-content-center',
                        `bg-${getEmailTypeColor(selectedEmail)}`
                      )}>
                        <UilUser size="30" className="text-white" />
                      </div>
                    </div>
                    
                    <div className="thread-email-meta">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <strong className="sender-name">
                          {selectedEmail.direction === 'sent' ? 'You' : selectedEmail.from}
                        </strong>
                        
                        {selectedEmail.is_ai_generated && (
                          <Badge bg="primary" className="ai-badge">
                            <UilBrain size="12" className="me-1" />
                            AI Generated {selectedEmail.draft_confidence && 
                              `(${Math.round(selectedEmail.draft_confidence * 100)}%)`
                            }
                          </Badge>
                        )}
                      </div>
                      
                      <div className="email-details text-muted">
                        <small>
                          To: {selectedEmail.direction === 'sent' ? selectedEmail.to : 'You'} • 
                          {' '}{dateFormatter(selectedEmail.email_creation_date)} • 
                          {' '}{getTimeAgo(selectedEmail.email_creation_date)}
                        </small>
                      </div>
                    </div>
                  </div>
                  
                  {/* <div className="d-flex align-items-center gap-2">
                    <small className="text-muted">
                      {dateFormatter(selectedEmail.email_creation_date)}
                    </small>
                  </div> */}
                </div>
              </div>

              {/* Email Body */}
              <div className="thread-email-body">
                <div 
                  className="email-content email-html-content"
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(selectedEmail.body || 'No content available', {
                      ADD_ATTR: ['style'], // Allow style attributes for HTML emails
                      ADD_TAGS: ['style'] // Allow style tags
                    })
                  }}
                  style={{ 
                    lineHeight: 1.6,
                    fontSize: '14px',
                    color: '#2c3e50',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    wordWrap: 'break-word'
                  }}
                />
              </div>

              {/* Email Actions */}
              <div className="thread-email-actions mt-4 pt-3 border-top">
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={handleReplyClick}
                    className="d-flex align-items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faReply} />
                    Reply
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    className="d-flex align-items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faForward} />
                    Forward
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => onDeleteEmail(selectedEmail.id)}
                    className="d-flex align-items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    Delete
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <h2 className="mb-4">Email Communication</h2>
      <Row className="align-items-center g-3 justify-content-between">
        <Col xs="12" sm="auto">
          <SearchBox
            placeholder="Search email"
            prepend={<FontAwesomeIcon icon={faSearch} className="text-muted" />}
            className="w-100"
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Col>
        <Col sm="auto">
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handleGenerateAIDraft()}
              className="d-flex align-items-center gap-2"
              disabled={isGeneratingDraft}
            >
              <FontAwesomeIcon icon={faBrain} />
              {isGeneratingDraft ? 'Generating...' : 'AI Draft'}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleComposeClick}
              className="d-flex align-items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              Compose
            </Button>
          </div>
        </Col>
      </Row>

      {/* Tab Navigation */}
      <div className="email-tabs d-flex flex-wrap gap-2 mb-3 mt-3" style={{ overflowX: 'auto' }}>
        <TabButton
          type="inbox"
          icon={faInbox}
          label="Inbox"
          count={inboxCount}
          isActive={activeTab === 'inbox'}
          onClick={() => {
            setActiveTab('inbox');
            onRefreshEmails('received');
          }}
        />
        <TabButton
          type="sent"
          icon={faPaperPlane}
          label="Sent"
          count={sentCount}
          isActive={activeTab === 'sent'}
          onClick={() => {
            setActiveTab('sent');
            onRefreshEmails('sent');
          }}
        />
        <TabButton
          type="drafts"
          icon={faBrain}
          label="AI Drafts"
          count={draftCount}
          isActive={activeTab === 'drafts'}
          onClick={() => {
            setActiveTab('drafts');
            onRefreshEmails('drafts');
          }}
        />
        <TabButton
          type="scheduled"
          icon={faClock}
          label="Scheduled"
          count={scheduledCount}
          isActive={activeTab === 'scheduled'}
          onClick={() => {
            setActiveTab('scheduled');
            onRefreshEmails('scheduled');
          }}
        />
      </div>

      <div className="d-flex flex-column gap-1">
        {status === 'loading' && !skipRefreshFlag ? (
          <div>
            {[...Array(5)].map((_, index) => (
              <Card 
                key={index} 
                className="w-100 email-card border-0 cursor-pointer email-list-compact d-flex flex-row align-items-start p-2"
                style={{ 
                  minHeight: '100px', 
                  maxHeight: '100px',
                  overflow: 'hidden',
                  width: '100%',
                  maxWidth: '100%', 
                }}
              >
                {/* Email Icon - matches real email icon structure */}
                <div className="email-icon-wrapper me-2 me-sm-3 flex-shrink-0">
                  <div className="email-icon d-flex align-items-center justify-content-center bg-primary"
                       style={{ width: 'clamp(24px, 5vw, 30px)', height: 'clamp(24px, 5vw, 30px)', borderRadius: '8px' }}>
                    <Placeholder animation="glow" className="m-0 p-0">
                      <Placeholder xs={12} style={{ width: '16px', height: '16px' }} className="text-white" />
                    </Placeholder>
                  </div>
                </div>
                
                {/* Email Content - matches real email content structure */}
                <div className="email-content flex-grow-1 min-w-0">
                  {/* Badges Row */}
                  <div className="d-flex align-items-center flex-wrap gap-1 gap-sm-2 mb-2">
                    <Placeholder animation="glow" className="m-0 p-0">
                      <Placeholder xs={12} style={{ width: '40px', height: '18px', borderRadius: '6px' }} />
                    </Placeholder>
                  </div>
                  
                  {/* Subject and Time Row */}
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-1 flex-wrap flex-sm-nowrap">
                    <Placeholder animation="glow" style={{ flex: '1 1 60%', minWidth: 0 }}>
                      <Placeholder xs={12} style={{ height: '1.2rem' }} />
                    </Placeholder>
                    <Placeholder animation="glow" className="text-nowrap">
                      <Placeholder xs={12} style={{ width: '60px', height: '0.75rem' }} />
                    </Placeholder>
                  </div>
                  
                  {/* Preview Text */}
                  <Placeholder animation="glow">
                    <Placeholder xs={10} style={{ height: '0.9rem', marginBottom: '0.25rem' }} />
                    <Placeholder xs={8} style={{ height: '0.8rem' }} />
                  </Placeholder>
                </div>
              </Card>
            ))}
          </div>
        ) : status !== 'loading' && filteredEmails.length > 0 ? (
          filteredEmails.map((email, index) => {
            const EmailIcon = getEmailIcon(email);
            const isUnread = !email.is_read && email.direction === 'received';
            
            return (
              <Card 
                key={email.id} 
                className={classNames(
                  'w-100 email-card border-0 cursor-pointer email-list-compact',
                  'd-flex flex-row align-items-start p-2',
                  {
                    'email-unread': isUnread,
                    'email-ai-generated': email.is_ai_generated
                  }
                )}
                onClick={() => handleEmailClick(email)}
                style={{ 
                  minHeight: '100px', 
                  maxHeight: '100px',
                  overflow: 'hidden',
                  width: '100%', // Full width on all devices
                  maxWidth: '100%', // Ensures it doesn't overflow
                }}
              >
                {/* Email Icon */}
                <div className="email-icon-wrapper me-2 me-sm-3 flex-shrink-0">
                  <div className={classNames(
                    'email-icon d-flex align-items-center justify-content-center',
                    `bg-${getEmailTypeColor(email)}`
                  )} style={{ width: 'clamp(24px, 5vw, 30px)', height: 'clamp(24px, 5vw, 30px)', borderRadius: '8px' }}>
                    <EmailIcon size="16" className="text-white" />
                  </div>
                </div>

                {/* Email Content */}
                <div className="email-content flex-grow-1 min-w-0">
                  {/* Badges Row */}
                  <div className="d-flex align-items-center flex-wrap gap-1 gap-sm-2 mb-2">
                    {email.is_ai_generated && (
                      <Badge bg="primary" className="email-badge" style={{ fontSize: 'clamp(0.65rem, 2vw, 0.7rem)', padding: '2px 6px' }}>AI</Badge>
                    )}
                    {email.direction === 'sent' && (
                      <Badge bg="success" className="email-badge" style={{ fontSize: 'clamp(0.65rem, 2vw, 0.7rem)', padding: '2px 6px' }}>Sent</Badge>
                    )}
                    {isUnread && (
                      <Badge bg="info" className="email-badge" style={{ fontSize: 'clamp(0.65rem, 2vw, 0.7rem)', padding: '2px 6px' }}>New</Badge>
                    )}
                    {email.direction === 'received' && !isUnread && (
                      <Badge bg="secondary" className="email-badge" style={{ fontSize: 'clamp(0.65rem, 2vw, 0.7rem)', padding: '2px 6px' }}>Received</Badge>
                    )}
                  </div>

                  {/* Subject and Time Row */}
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-1 flex-wrap flex-sm-nowrap">
                    <h6 className={classNames(
                      'email-subject mb-0 text-truncate',
                      { 'fw-bold': isUnread }
                    )} style={{ 
                      fontSize: 'clamp(0.85rem, 3vw, 0.95rem)',
                      lineHeight: '1.3',
                      minWidth: 0, // Allows the h6 to shrink and truncate
                      flex: '1 1 60%' // Allows the title to take more space on small screens
                    }}>
                      {email.subject || '(No Subject)'}
                    </h6>
                    <small className="text-muted text-nowrap" style={{ fontSize: 'clamp(0.7rem, 2vw, 0.75rem)' }}>
                      {getTimeAgo(email.email_creation_date)}
                    </small>
                  </div>

                  {/* Email Body Preview */}
                  <div className="email-preview">
                    <p className="mb-0 text-muted" style={{ 
                      fontSize: 'clamp(0.75rem, 2.5vw, 0.8rem)',
                      lineHeight: '1.3',
                      maxHeight: '6.5rem', /* Increased height to show more content */
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 5, /* Increased from 4 to 5 lines */
                      WebkitBoxOrient: 'vertical' as const
                    }}>
                      {email.body ? 
                        ((text) => {
                          const plainText = text.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
                          return plainText.length > 350 
                            ? `${plainText.substring(0, 350)}...` 
                            : plainText || 'No content available';
                        })(email.body) :
                        'No content available'
                      }
                    </p>
                  </div>
                </div>
              </Card>
            );
          })
        ) : (
          <Card className="text-center py-5">
            <Card.Body>
              <UilInbox size="48" className="text-muted mb-3" />
              <h5 className="text-muted">No emails found</h5>
              <p className="text-muted mb-4">
                {activeTab === 'drafts' 
                  ? 'Generate your first AI draft to get started'
                  : 'No emails in this folder'
                }
              </p>
              {activeTab === 'drafts' && (
                <Button 
                  variant="primary" 
                  onClick={handleGenerateAIDraft}
                  className="d-flex align-items-center gap-2 mx-auto"
                  disabled={isGeneratingDraft}
                >
                  <FontAwesomeIcon icon={faBrain} />
                  {isGeneratingDraft ? 'Generating...' : 'Generate AI Draft'}
                </Button>
              )}
            </Card.Body>
          </Card>
        )}
      </div>

      {/* Compose Email Modal */}
      <Modal show={showComposeModal} onHide={() => setShowComposeModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <UilMessage size="24" className="text-primary" />
            Compose Email
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmitEmail)}>
            <div className="condensed-email-form">
              {/* TO and CC Fields */}
              <div className="recipient-fields mb-3">
                <div className="d-flex align-items-center mb-2">
                  <div className="flex-grow-1">
                    <RecipientInput
                      label="To"
                      recipients={toRecipients}
                      onAddRecipient={(recipient) => setToRecipients([...toRecipients, recipient])}
                      onRemoveRecipient={(index) => setToRecipients(toRecipients.filter((_, i) => i !== index))}
                      placeholder="Enter recipient email address"
                      variant="primary"
                      required
                      error={errors.to?.message}
                    />
                  </div>
                  {!showBccCompose && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-decoration-none ms-3 p-0"
                      onClick={() => setShowBccCompose(true)}
                      style={{ fontSize: '0.85rem', color: '#6c757d' }}
                    >
                      BCC
                    </Button>
                  )}
                </div>
                
                <RecipientInput
                  label="Cc"
                  recipients={ccRecipients}
                  onAddRecipient={(recipient) => setCcRecipients([...ccRecipients, recipient])}
                  onRemoveRecipient={(index) => setCcRecipients(ccRecipients.filter((_, i) => i !== index))}
                  placeholder="Add CC recipients"
                  variant="secondary"
                />
                
                {showBccCompose && (
                  <RecipientInput
                    label="Bcc"
                    recipients={bccRecipients}
                    onAddRecipient={(recipient) => setBccRecipients([...bccRecipients, recipient])}
                    onRemoveRecipient={(index) => setBccRecipients(bccRecipients.filter((_, i) => i !== index))}
                    placeholder="Add BCC recipients"
                    variant="success"
                  />
                )}
              </div>
              
              {/* Subject and Schedule Fields */}
              <div className="mb-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 me-2" style={{ width: '65px' }}>
                        <span className="text-muted fw-normal" style={{ fontSize: '0.9rem' }}>
                          Subject:
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <Form.Control
                          type="text"
                          placeholder="Email subject"
                          {...register("subject", { required: "Subject is required" })}
                          className="border-light bg-light"
                          style={{ fontSize: '0.9rem' }}
                        />
                        {errors.subject && (
                          <small className="text-danger ms-0">{errors.subject.message}</small>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* <div style={{ width: '200px' }}>
                    <Form.Control
                      type="datetime-local"
                      {...register("schedule_date")}
                      className="border-light bg-light"
                      style={{ fontSize: '0.85rem' }}
                      title="Schedule for later (optional)"
                    />
                  </div> */}
                </div>
              </div>
              
              <Col md="12">
                <Form.Label className="fw-semibold">Message</Form.Label>
                <ReactQuill
                  style={{ height: "200px", marginBottom: "3rem" }}
                  theme="snow"
                  value={watchMessage}
                  onChange={(value) => setValue("message", value)}
                  placeholder="Write your message here..."
                />
                {errors.message && (
                  <small className="text-danger">{errors.message.message}</small>
                )}
              </Col>
              
              {/* Attachments Section */}
              <Col md="12">
                <div style={{ paddingTop: '2rem' }}>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted fw-normal" style={{ fontSize: '0.9rem' }}>
                      Attachments:
                    </span>
                    <div>
                      <input
                        type="file"
                        multiple
                        onChange={(e) => handleFileAttachment(e.target.files, false)}
                        style={{ display: 'none' }}
                        id="compose-file-input"
                      />
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => document.getElementById('compose-file-input')?.click()}
                        className="d-flex align-items-center gap-1"
                      >
                        <FontAwesomeIcon icon={faPaperPlane} size="sm" />
                        Attach Files
                      </Button>
                    </div>
                  </div>
                  
                  {attachments.length > 0 && (
                    <div className="attachment-list">
                      {attachments.map((file, index) => (
                        <div key={index} className="attachment-item d-flex align-items-center justify-content-between p-2 border rounded mb-1 bg-light">
                          <div className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faPaperPlane} className="text-muted" />
                            <div>
                              <span className="fw-medium" style={{ fontSize: '0.85rem' }}>{file.name}</span>
                              <small className="text-muted ms-2">({formatFileSize(file.size)})</small>
                            </div>
                          </div>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => removeAttachment(index, false)}
                            className="text-danger p-0"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            <Button
              variant="outline-primary"
              onClick={handleGenerateAIDraft}
              disabled={isGeneratingDraft}
              className="d-flex align-items-center gap-2"
            >
              <FontAwesomeIcon icon={faMagic} />
              {isGeneratingDraft ? 'Generating...' : 'AI Assist'}
            </Button>
          </div>
          
          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={() => setShowComposeModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit(onSubmitEmail)}
              disabled={!watchMessage.trim() || isSendingEmail}
            >
              {isSendingEmail ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                </>
              ) : (
                'Send Email'
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default EmailInterface; 